// pricingBasis.js — mirrors backend/services/pricingBasis.js exactly, for
// the live on-screen preview on the Quote/Sales Order forms before Save
// re-resolves for real. Keep the two in step.
//
// Business: unit price ex, GST added on top. Retail: the ex price grossed
// up and rounded to the Tax Handling Type's retail_rounding_cents — that
// rounded inc price is the real price, and ex/GST are taken out of it.

const round4 = (n) => Number(Number(n).toFixed(4));
const round2 = (n) => Math.round(Number(n) * 100 + Number.EPSILON) / 100;

export function basisOf(taxHandlingType) {
  const cents = Number(taxHandlingType?.retail_rounding_cents);
  return {
    retail: taxHandlingType?.pricing_basis === 'retail',
    roundingCents: [1, 5, 10].includes(cents) ? cents : 1,
  };
}

// Rounds an inc price to the nearest roundingCents, half up.
export function roundRetail(value, roundingCents = 1) {
  const cents = Math.round(Number(value) * 100 * 1e6) / 1e6;
  return (Math.round(cents / roundingCents) * roundingCents) / 100;
}

const present = (v) => v !== null && v !== undefined && v !== '';

// One line's figures, camelCased the way the forms hold a line.
export function priceLine({ unitPriceEx, unitPriceInc, qty, gstRate, basis }) {
  const rate = Number(gstRate) || 0;
  const q = Number(qty) || 0;
  if (!present(unitPriceInc) && !present(unitPriceEx)) {
    return { unitPrice: null, unitPriceInc: null, extendedPrice: null, gstAmount: null, extendedPriceIncGst: null };
  }
  if (basis.retail) {
    const inc = present(unitPriceInc) ? round4(unitPriceInc) : roundRetail(Number(unitPriceEx) * (1 + rate / 100), basis.roundingCents);
    const extendedInc = round4(inc * q);
    const gst = round4(extendedInc * (rate / (100 + rate)));
    return {
      unitPrice: round4(inc / (1 + rate / 100)),
      unitPriceInc: inc,
      extendedPrice: round4(extendedInc - gst),
      gstAmount: gst,
      extendedPriceIncGst: extendedInc,
    };
  }
  const ex = present(unitPriceEx) ? Number(unitPriceEx) : round4(Number(unitPriceInc) / (1 + rate / 100));
  const extended = round4(ex * q);
  const gst = round4(extended * (rate / 100));
  return { unitPrice: ex, unitPriceInc: null, extendedPrice: extended, gstAmount: gst, extendedPriceIncGst: round4(extended + gst) };
}

// A form line's unit price in its Customer's basis.
export function shownUnitPrice(line, gstRate, basis) {
  if (!present(line.unitPrice) && !present(line.unitPriceInc)) return null;
  if (!basis.retail) return Number(line.unitPrice);
  if (present(line.unitPriceInc)) return Number(line.unitPriceInc);
  return roundRetail(Number(line.unitPrice) * (1 + (Number(gstRate) || 0) / 100), basis.roundingCents);
}

// A form line's amount in its Customer's basis.
export function shownLineAmount(line, basis) {
  const ex = Number(line.extendedPrice || 0);
  if (!basis.retail) return ex;
  return present(line.extendedPriceIncGst) ? Number(line.extendedPriceIncGst) : ex + Number(line.gstAmount || 0);
}

// A Sales Order form line with its cancelled quantity taken out of its
// amounts, so a cancelled backorder no longer counts in the Line total or
// the document's totals. Ordered qty is left as it is. See netOfCancelled
// in backend/services/pricingBasis.js.
export function netOfCancelled(line) {
  const qty = Number(line.qty) || 0;
  const cancelled = Number(line.qtyCancelled) || 0;
  if (!(cancelled > 0) || !(qty > 0)) return line;
  const share = Math.max(qty - cancelled, 0) / qty;
  const scale = (v) => (present(v) ? round4(Number(v) * share) : v);
  return { ...line, extendedPrice: scale(line.extendedPrice), gstAmount: scale(line.gstAmount), extendedPriceIncGst: scale(line.extendedPriceIncGst) };
}

// An Order Discount's amount on these lines, in the Customer's basis. See
// orderDiscountAmount in backend/services/pricingBasis.js.
export function orderDiscountAmount(discount, linesAmount) {
  const value = Number(discount?.value);
  if (!discount?.type || !(value > 0)) return 0;
  const lines = Math.max(Number(linesAmount) || 0, 0);
  return discount.type === 'percent' ? Math.min(round2(lines * (value / 100)), lines) : Math.min(round2(value), lines);
}

// Document totals — GST rounded once for the whole document, never per
// line. See documentTotals in backend/services/pricingBasis.js.
export function documentTotals({ lines, shippingEx = 0, optionsEx = 0, gstRate, basis, discount = null }) {
  const rate = Number(gstRate) || 0;
  const shipEx = Number(shippingEx) || 0;
  const optEx = Number(optionsEx) || 0;
  const shippingInc = round2(shipEx * (1 + rate / 100));
  const optionsInc = round2(optEx * (1 + rate / 100));
  if (basis.retail) {
    const linesInc = lines.reduce((sum, l) => sum + shownLineAmount(l, basis), 0);
    const off = orderDiscountAmount(discount, round2(linesInc));
    const total = round2(linesInc - off + shippingInc + optionsInc);
    return { linesAmount: round2(linesInc), discount: off, shipping: shippingInc, options: optionsInc, gst: round2(total * (rate / (100 + rate))), total };
  }
  const subtotal = round2(lines.reduce((sum, l) => sum + Number(l.extendedPrice || 0), 0));
  const linesGst = lines.reduce((sum, l) => sum + Number(l.gstAmount || 0), 0);
  const off = orderDiscountAmount(discount, subtotal);
  const linesKept = subtotal > 0 ? (subtotal - off) / subtotal : 1;
  const gst = round2(linesGst * linesKept + (shipEx + optEx) * (rate / 100));
  return { linesAmount: subtotal, discount: off, shipping: shippingInc, options: optionsInc, gst, total: round2(round2(subtotal - off + shipEx + optEx) + gst) };
}
